import React, { useState } from 'react';

import { Box, Button, Stack, Text } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { ParameterRow } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/ParameterRow';
import { JsonProperty } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/properties/types';
import { EditingState } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/types';
import { ValidationUtils } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/CustomApiRequestToolConfigurationPanel/JsonSchemaBuilder/utils';
import { useTranslation } from 'react-i18next';

interface ParameterGroupProps {
  properties: Record<string, JsonProperty>;
  required?: string[];
  path: string[];
  level: number;
  onEdit: (fullPath: string[]) => void;
  onDelete: (fullPath: string[]) => void;
  onAddNested: (parentPath: string[]) => void;
  onToggleRequired?: (fullPath: string[], isRequired: boolean) => void;
  editingState?: EditingState;
  editorComponent?: React.ReactNode;
}

export function ParameterGroup({
  properties,
  required = [],
  path,
  level,
  onEdit,
  onDelete,
  onAddNested,
  onToggleRequired,
  editingState,
  editorComponent
}: ParameterGroupProps) {
  const { t } = useTranslation();
  const entries = Object.entries(properties);

  // Auto-expand objects and arrays on initial load to show structure and "Add Property" buttons
  const [expandedItems, setExpandedItems] = useState<Set<string>>(() => {
    const autoExpanded = new Set<string>();
    const checkProperties = (props: Record<string, JsonProperty>, currentPath: string[]) => {
      Object.entries(props).forEach(([key, property]) => {
        const itemPath = [...currentPath, key];
        if (property.type === 'array') {
          autoExpanded.add(itemPath.join('.'));
        }
        if (property.type === 'object') {
          autoExpanded.add(itemPath.join('.'));
        }
        if (property.type === 'array' && property.items?.type === 'object') {
          const itemsPath = [...itemPath, 'items'].join('.');
          autoExpanded.add(itemsPath);
        }
        if (property.type === 'object' && property.properties) {
          checkProperties(property.properties, itemPath);
        }
        if (property.type === 'array' && property.items?.type === 'object' && property.items.properties) {
          checkProperties(property.items.properties, [...itemPath, 'items']);
        }
      });
    };
    checkProperties(properties, path);
    return autoExpanded;
  });

  const toggleExpanded = (fullPath: string[]) => {
    const pathKey = fullPath.join('.');
    setExpandedItems((prev: Set<string>) => {
      const newSet = new Set(prev);
      if (newSet.has(pathKey)) {
        newSet.delete(pathKey);
      } else {
        newSet.add(pathKey);
      }
      return newSet;
    });
  };

  // Helper function to check if we're adding at this exact level
  const isAddingAtThisLevel = (targetPath: string[]) => {
    return (
      editingState?.mode === 'add' &&
      editingState.path.length === targetPath.length &&
      editingState.path.every((segment: string, index: number) => segment === targetPath[index])
    );
  };

  // Helper function to check if we're editing a specific property
  const isEditingProperty = (propertyKey: string, propertyPath: string[]) => {
    if (editingState?.mode !== 'edit') return false;

    const fullPath = [...propertyPath, propertyKey];
    return (
      editingState.path.length === fullPath.length &&
      editingState.path.every((segment: string, index: number) => segment === fullPath[index])
    );
  };

  return (
    <Stack spacing="sm">
      {entries.map(([key, property]) => {
        const currentPath = [...path, key];
        const isRequired = required.includes(key);
        const isEditingThisProperty = isEditingProperty(key, path);

        return (
          <Box key={key} style={{ marginLeft: level * 16 }}>
            {isEditingThisProperty ? (
              editorComponent
            ) : (
              <ParameterRow
                name={key}
                property={property}
                fullPath={currentPath}
                isRequired={isRequired}
                onEdit={onEdit}
                onDelete={onDelete}
                onToggleRequired={onToggleRequired}
                isExpanded={expandedItems.has(currentPath.join('.'))}
                onToggleExpanded={toggleExpanded}
              />
            )}

            {property.type === 'object' && expandedItems.has(currentPath.join('.')) && (
              <Box mt="xs" ml="md">
                <Box
                  pl="md"
                  sx={(theme) => ({
                    borderLeft: `2px solid ${theme.colors.gray[2]}`,
                    position: 'relative'
                  })}>
                  {property.properties && (
                    <ParameterGroup
                      properties={property.properties}
                      required={property.required}
                      path={currentPath}
                      level={level + 1}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onAddNested={onAddNested}
                      onToggleRequired={onToggleRequired}
                      editingState={editingState}
                      editorComponent={editorComponent}
                    />
                  )}

                  {editingState?.mode !== 'add' && (
                    <Box py="sm" style={{ marginLeft: (level + 1) * 16 }}>
                      {ValidationUtils.validateNestingLevel([...currentPath, 'newProperty']) ? (
                        <Button
                          size="xs"
                          variant="outline"
                          leftIcon={<IconPlus size={12} />}
                          onClick={() => onAddNested(currentPath)}>
                          {t('JSON_SCHEMA_BUILDER.ADD_PROPERTY')}
                        </Button>
                      ) : (
                        <Text size="xs" c="dimmed" fs="italic">
                          {t('JSON_SCHEMA_BUILDER.MAXIMUM_NESTING_REACHED')}
                        </Text>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {property.type === 'array' && property.items && expandedItems.has(currentPath.join('.')) && (
              <Box mt="sm" style={{ marginLeft: (level + 1) * 16 }}>
                {isEditingProperty('items', currentPath) ? (
                  editorComponent
                ) : (
                  <ParameterRow
                    name={t('JSON_SCHEMA_BUILDER.ARRAY_ITEM')}
                    property={property.items}
                    fullPath={[...currentPath, 'items']}
                    isRequired={true}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onToggleRequired={onToggleRequired}
                    isExpanded={expandedItems.has([...currentPath, 'items'].join('.'))}
                    onToggleExpanded={toggleExpanded}
                    isDeletable={false}
                  />
                )}

                {/* Array item objects - duplicate logic from object handling above for consistency */}
                {property.items.type === 'object' && expandedItems.has([...currentPath, 'items'].join('.')) && (
                  <Box mt="xs" ml="md">
                    <Box
                      pl="md"
                      sx={(theme) => ({
                        borderLeft: `2px solid ${theme.colors.gray[2]}`,
                        position: 'relative'
                      })}>
                      {property.items.properties && (
                        <ParameterGroup
                          properties={property.items.properties}
                          required={property.items.required}
                          path={[...currentPath, 'items']}
                          level={level + 2}
                          onEdit={onEdit}
                          onDelete={onDelete}
                          onAddNested={onAddNested}
                          onToggleRequired={onToggleRequired}
                          editingState={editingState}
                          editorComponent={editorComponent}
                        />
                      )}

                      {editingState?.mode !== 'add' && (
                        <Box py="sm" style={{ marginLeft: (level + 2) * 16 }}>
                          {ValidationUtils.validateNestingLevel([...currentPath, 'items', 'newProperty']) ? (
                            <Button
                              size="xs"
                              variant="outline"
                              leftIcon={<IconPlus size={12} />}
                              onClick={() => onAddNested([...currentPath, 'items'])}>
                              {t('JSON_SCHEMA_BUILDER.ADD_PROPERTY')}
                            </Button>
                          ) : (
                            <Text size="xs" c="dimmed" fs="italic">
                              {t('JSON_SCHEMA_BUILDER.MAXIMUM_NESTING_REACHED')}
                            </Text>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        );
      })}

      {isAddingAtThisLevel(path) && <Box style={{ marginLeft: level * 16 }}>{editorComponent}</Box>}
    </Stack>
  );
}
